import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, message, Drawer, Button, Modal, Tooltip } from "antd";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { CaseForm } from ".";
import {
  removeUndefinedObjectValue,
  getFieldDisableStatusForCommodity,
  api,
} from "../../../lib";
import dayjs from "dayjs";
import {
  CaseUIState,
  CurrentCaseState,
  PrevCaseState,
  stepPath,
  resetCurrentCaseState,
} from "../store";
import { isEqual, isEmpty } from "lodash";
import { UserState } from "../../../store";
import { countryOptions, focusCommodityOptions } from "../../../store/static";
import { CustomEvent } from "@piwikpro/react-piwik-pro";
import { routePath } from "../../../components/route";
import { MAX_SEGMENT } from "../constants";

const dataUploadFieldPreffix = "data_upload_";

const CaseSettings = ({ open = false, handleCancel = () => {} }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const prevCase = PrevCaseState.useState((s) => s);
  const currentCase = CurrentCaseState.useState((s) => s);
  const { secondary, tertiary, general } = CaseUIState.useState((s) => s);
  const { enableEditCase, activeSegmentId } = general;

  const [prevCaseSettingValue, setPrevCaseSettingValue] = useState({});
  const [formData, setFormData] = useState({ segments: [""] });
  const [isSaving, setIsSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const { internal_user: userInternal } = UserState.useState((s) => s);

  const [deletedSegmentIds, setDeletedSegmentIds] = useState([]);
  const segmentFields = Form.useWatch("segments", form);
  const [activeTab, setActiveTab] = useState("manual");
  const importId = Form.useWatch("import_id", form);

  const hasSegments = useMemo(() => {
    return (
      segmentFields?.some((s) =>
        typeof s === "string" ? s.trim() !== "" : !!s?.name?.trim()
      ) || false
    );
  }, [segmentFields]);

  const isUploadMissing = activeTab === "upload" && !importId && !hasSegments;
  const isSaveDisabled =
    !enableEditCase ||
    (segmentFields?.length || 0) > MAX_SEGMENT ||
    isUploadMissing;

  const handleCancelWithGuard = useCallback(() => {
    const isDirty =
      form.isFieldsTouched() || !!importId || deletedSegmentIds.length > 0;

    if (isDirty) {
      Modal.confirm({
        title: "Unsaved Changes",
        icon: <ExclamationCircleOutlined />,
        content:
          "You have unsaved changes. Are you sure you want to discard them?",
        okText: "Discard changes",
        okType: "danger",
        cancelText: "Keep editing",
        onOk: () => {
          // server side cleanup if importId present
          if (importId) {
            api
              .delete(`/case-import/${importId}`)
              .then(() => {})
              .catch(() => {});
          }
          // reset deleted segment on discard
          if (deletedSegmentIds?.length) {
            form.setFieldValue("segments", formData.segments);
          }
          // reset global state on discard
          if (currentCase?.id) {
            CurrentCaseState.update((s) => ({
              ...s,
              ...prevCase,
            }));
          } else {
            resetCurrentCaseState();
          }
          form.resetFields();
          handleCancel();
        },
      });
    } else {
      handleCancel();
    }
  }, [
    form,
    handleCancel,
    deletedSegmentIds,
    formData.segments,
    importId,
    currentCase?.id,
    prevCase,
  ]);

  const updateCurrentCase = useCallback((key, value) => {
    CurrentCaseState.update((s) => ({
      ...s,
      [key]: value,
    }));
  }, []);

  const updateCaseUI = useCallback((key, value) => {
    CaseUIState.update((s) => ({
      ...s,
      [key]: value,
    }));
  }, []);

  const setNewCpiModalVisible = (value) => {
    CaseUIState.update((s) => ({
      ...s,
      stepSetIncomeTarget: {
        ...s.stepSetIncomeTarget,
        newCpiModalVisible: value,
      },
    }));
  };

  const setNewCPIState = ({ newCPI, newCPIFactor }) => {
    CaseUIState.update((s) => ({
      ...s,
      stepSetIncomeTarget: {
        ...s.stepSetIncomeTarget,
        newCPI,
        newCPIFactor,
      },
    }));
  };

  const isOnStep1Page = useMemo(() => {
    return window.location.pathname?.includes(stepPath.step1.label);
  }, []);

  useEffect(
    () => {
      setPrevCaseSettingValue(formData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (currentCase?.id || !currentCase?.id) {
      form.resetFields();
      setFormData({ segments: [""] });
    }
  }, [currentCase?.id, form]);

  useEffect(() => {
    // handle initial load
    if (currentCase.id) {
      // focus commodity
      const focusCommodityValue = {
        name: currentCase.name,
        description: currentCase.description,
        private: currentCase?.private || false,
        tags: currentCase?.tags || [],
        country: currentCase.country,
        focus_commodity: currentCase.focus_commodity,
        year: dayjs(String(currentCase.year)),
        currency: currentCase.currency,
        area_size_unit: currentCase.area_size_unit,
        volume_measurement_unit: currentCase.volume_measurement_unit,
        reporting_period: currentCase.reporting_period,
        company: currentCase.company,
        segments: currentCase?.segments?.length
          ? currentCase.segments.map((s) => ({
              id: s.id,
              name: s.name,
              number_of_farmers: s.number_of_farmers,
            }))
          : [""],
      };
      // secondary
      let secondaryCommodityValue = {};
      const secondaryCommodityTmp = currentCase.case_commodities.find(
        (val) => val.commodity_type === "secondary"
      );
      if (secondaryCommodityTmp) {
        let disableAreaSizeField = true;
        Object.keys(secondaryCommodityTmp).forEach((key) => {
          let val = secondaryCommodityTmp[key];
          if (key === "breakdown") {
            val = val ? 1 : 0;
            disableAreaSizeField = val ? false : true;
          }
          secondaryCommodityValue = {
            ...secondaryCommodityValue,
            [`secondary-${key}`]: val,
          };
        });
        const { disableLandUnitField, disableDataOnIncomeDriverField } =
          getFieldDisableStatusForCommodity(secondaryCommodityTmp?.commodity);
        // update case UI state
        updateCaseUI("secondary", {
          enable: true,
          disableAreaSizeField: disableAreaSizeField,
          disableLandUnitField: disableLandUnitField,
          disableDataOnIncomeDriverField: disableDataOnIncomeDriverField,
        });
      }
      // tertiary
      let tertiaryCommodityValue = {};
      const tertiaryCommodityTmp = currentCase.case_commodities.find(
        (val) => val.commodity_type === "tertiary"
      );
      if (tertiaryCommodityTmp) {
        let disableAreaSizeField = true;
        Object.keys(tertiaryCommodityTmp).forEach((key) => {
          let val = tertiaryCommodityTmp[key];
          if (key === "breakdown") {
            val = val ? 1 : 0;
            disableAreaSizeField = val ? false : true;
          }
          tertiaryCommodityValue = {
            ...tertiaryCommodityValue,
            [`tertiary-${key}`]: val,
          };
        });
        const { disableLandUnitField, disableDataOnIncomeDriverField } =
          getFieldDisableStatusForCommodity(tertiaryCommodityTmp?.commodity);
        // update case UI state
        updateCaseUI("tertiary", {
          enable: true,
          disableAreaSizeField: disableAreaSizeField,
          disableLandUnitField: disableLandUnitField,
          disableDataOnIncomeDriverField: disableDataOnIncomeDriverField,
        });
      }
      // set initial value
      const formDataTmp = {
        ...focusCommodityValue,
        ...secondaryCommodityValue,
        ...tertiaryCommodityValue,
      };
      setFormData(formDataTmp);
    }
  }, [currentCase, updateCaseUI, form]);

  const onValuesChange = (changedValues) => {
    // secondary breakdown handle
    if (changedValues?.["secondary-breakdown"] === 0) {
      form.setFieldsValue({
        ["secondary-area_size_unit"]: null,
        ["secondary-volume_measurement_unit"]: null,
      });
    }
    // tertiary breakdown handle
    if (changedValues?.["tertiary-breakdown"] === 0) {
      form.setFieldsValue({
        ["tertiary-area_size_unit"]: null,
        ["tertiary-volume_measurement_unit"]: null,
      });
    }
  };

  const saveCaseSettings = (values, deleteSegment = false) => {
    const other_commodities = [];

    if (secondary.enable) {
      // add secondary crop into case commodities
      const secondaryCommodityValue = {
        commodity: values["secondary-commodity"],
        breakdown: values["secondary-breakdown"] ? true : false,
        currency: values.currency,
        area_size_unit: values["secondary-area_size_unit"],
        volume_measurement_unit: values["secondary-volume_measurement_unit"],
        commodity_type: "secondary",
      };
      other_commodities.push(secondaryCommodityValue);
    }

    if (tertiary.enable) {
      // add tertiary crop into case commodities
      const tertiaryCommodityValue = {
        commodity: values["tertiary-commodity"],
        breakdown: values["tertiary-breakdown"] ? true : false,
        currency: values.currency,
        area_size_unit: values["tertiary-area_size_unit"],
        volume_measurement_unit: values["tertiary-volume_measurement_unit"],
        commodity_type: "tertiary",
      };
      other_commodities.push(tertiaryCommodityValue);
    }

    const payload = {
      name: values.name,
      description: values.description,
      country: values.country,
      focus_commodity: values.focus_commodity,
      year: dayjs(values.year).year(),
      currency: values.currency,
      area_size_unit: values.area_size_unit,
      volume_measurement_unit: values.volume_measurement_unit,
      multiple_commodities: secondary.enable || tertiary.enable,
      reporting_period: "per-year",
      cost_of_production_unit: "cost_of_production_unit",
      segmentation: true,
      living_income_study: null,
      logo: null,
      private: currentCase.private,
      tags: values.tags || null,
      company: values.company || null,
      other_commodities: other_commodities,
      segments: form.getFieldValue("segments") || values.segments,
      import_id: values?.import_id || null,
    };

    // detect is payload updated
    const filteredPrevValue = removeUndefinedObjectValue(prevCaseSettingValue);
    const filteredCurrentValue = {
      ...filteredPrevValue,
      ...removeUndefinedObjectValue(values),
      private: currentCase.private,
      reporting_period: "per-year",
    };
    const isUpdated = !isEqual(filteredPrevValue, filteredCurrentValue);
    // EOL detect is payload updated

    const isNewCase = isEmpty(currentCase?.id);
    const apiCall = currentCase.id
      ? api.put(`case/${currentCase.id}?updated=${isUpdated}`, payload)
      : api.post("case", payload);

    apiCall
      .then((res) => {
        let data = res?.data || {};
        data = {
          ...data,
          segments: data?.segments?.length ? data.segments : [],
        };

        // NEW 23-06-2026 :: set form segments id fields value after saving the data
        const currentSegments = form.getFieldValue("segments") || [];
        const segmentsWithID = data?.segments?.length ? data.segments : [];
        const updatedSegmentFields = currentSegments?.map((segment) => {
          const findSegmentByName = segmentsWithID?.find(
            (s) => s?.name?.toLowerCase() === segment?.name?.toLowerCase()
          );
          if (findSegmentByName?.id) {
            return {
              ...segment,
              id: findSegmentByName.id,
              name: findSegmentByName.name,
            };
          }
          return segment;
        });
        form.setFieldsValue({
          import_id: data?.import_id || null,
          segments: updatedSegmentFields,
        });
        // EOL NEW 23-06-2026 set form segments id fields value after saving the data

        // track event: external user create new case (PoC)
        if (!userInternal && isNewCase) {
          const reportedCountry = countryOptions.find(
            (co) => co.value === data.country
          );
          const reportedCommodity = focusCommodityOptions.find(
            (fc) => fc.value === data.focus_commodity
          );
          const countryValue = reportedCountry
            ? reportedCountry.label
            : data.country;
          const commodityValue = reportedCommodity
            ? reportedCommodity.label
            : data.focus_commodity;

          CustomEvent.trackEvent(
            "Case Overview",
            "Create new case",
            "External users Country wise",
            1,
            {
              dimension3: countryValue,
            }
          );
          CustomEvent.trackEvent(
            "Case Overview",
            "Create new case",
            "External users Commodity wise",
            1,
            {
              dimension4: commodityValue,
            }
          );

          // Matomo custom event track
          if (window._paq) {
            // 1️⃣ Country-wise aggregation (FREE Matomo usable)
            window._paq.push([
              "trackEvent",
              "Case Overview",
              `Create case - External - Country: ${countryValue}`, // Action
              "", // Label (unused)
              1,
            ]);

            // 2️⃣ Commodity-wise aggregation (FREE Matomo usable)
            window._paq.push([
              "trackEvent",
              "Case Overview",
              `Create case - External - Commodity: ${commodityValue}`, // Action
              "",
              1,
            ]);

            // 3️⃣ Optional clean event for future paid reporting
            window._paq.push([
              "trackEvent",
              "Case Overview",
              "Create case",
              `user_type=external|country=${countryValue}|commodity=${commodityValue}`,
              1,
            ]);
          }
        }
        // EOL track event

        setPrevCaseSettingValue(filteredCurrentValue);

        const lowestSegmentId = data?.segments?.[0]?.id;
        const newActiveSegmentId = deleteSegment
          ? lowestSegmentId
          : activeSegmentId
          ? activeSegmentId
          : lowestSegmentId;

        const activeSegment = data?.segments?.find(
          (s) => s.id === newActiveSegmentId
        );
        const benchmark = activeSegment?.benchmark || {};

        let updatedData = { ...data };
        // update segment region value
        // this handle when the segment value not saved yet to DB
        // but the Case Settings is updated, so we use the currectCase state value
        updatedData = {
          ...updatedData,
          segments: updatedData?.segments?.map((segment) => {
            const findCurrentSegment = currentCase?.segments?.find(
              (s) => s.id === segment.id
            );
            if (findCurrentSegment) {
              return {
                ...segment,
                child: findCurrentSegment?.child || null,
                adult: findCurrentSegment?.adult || null,
                region: findCurrentSegment?.region || null,
                target: findCurrentSegment?.target || 0,
              };
            }
            return segment;
          }),
        };
        // EOL update segment region value

        // handle when year / country/ currency updated
        const {
          year: prevYear,
          country: prevCountry,
          currency: prevCurrency,
        } = prevCase;
        const {
          year: currYear,
          country: currCountry,
          currency: currCurrency,
        } = data;
        if (prevCountry !== currCountry) {
          updatedData = {
            ...updatedData,
            segments: updatedData?.segments?.map((segment) => {
              return {
                ...segment,
                child: null,
                adult: null,
                region: null,
                target: null,
                benchmark: null,
              };
            }),
          };
        }
        if (prevYear !== currYear || prevCurrency !== currCurrency) {
          updatedData = {
            ...updatedData,
            segments: updatedData?.segments?.map((segment) => {
              return {
                ...segment,
                target: null,
                benchmark: null,
              };
            }),
          };
        }
        // EOL handle when year / country / currency updated

        // handle trigger new CPI adjustment modal
        // show when cpi_factor = null
        if (isOnStep1Page) {
          const benchmarkValue =
            benchmark?.value?.[data?.currency?.toLowerCase()] ||
            benchmark?.value?.lcu;
          if (
            benchmarkValue &&
            benchmark?.cpi_factor === null &&
            benchmark?.year !== data.year
          ) {
            updatedData = {
              ...data,
              segments: data?.segments?.map((segment) => {
                if (segment.id === newActiveSegmentId) {
                  return {
                    ...segment,
                    target: 0,
                  };
                }
                return segment;
              }),
            };
            setNewCpiModalVisible(true);
            setNewCPIState({ newCPI: null, newCPIFactor: null });
          } else {
            setNewCpiModalVisible(false);
          }
        }
        // EOL handle trigger new CPI adjustment modal

        // update global state
        CurrentCaseState.update((s) => ({
          ...s,
          ...updatedData,
        }));
        PrevCaseState.update((s) => ({
          ...s,
          ...data,
        }));
        updateCaseUI("general", {
          ...general,
          activeSegmentId: newActiveSegmentId,
        });
        // EOL update global state

        messageApi.open({
          type: "success",
          content: "Case setting saved successfully.",
        });

        // SAVE /case-import/generate-segment-values
        if (values?.import_id) {
          // determine segmentation variable fallback from segments if global variable is null
          // this handles the case where segments are added via inline generators without a global selection
          const firstSegmentWithVar = values.segments?.find(
            (s) => s.segmentation_variable
          );
          const fallbackVar = firstSegmentWithVar?.segmentation_variable;

          const confirmedSegPayload = {
            case_id: data.id,
            import_id: values.import_id,
            segmentation_variable:
              values.data_upload_segmentation_variable || fallbackVar,
            segments: values.segments.map((seg, idx) => {
              // find segment id from data.segments
              const findSegment = data.segments.find(
                (s) => s.name === seg.name
              );
              const sVarType =
                seg.variable_type || values.data_upload_variable_type;
              const isNumerical = sVarType === "numerical";
              return {
                id: findSegment ? findSegment.id : null,
                index: idx,
                name: seg.name,
                value: seg.value || 0,
                min: isNumerical ? seg.min : null,
                max: isNumerical ? seg.value : null,
                number_of_farmers: seg.number_of_farmers || 0,
                is_manual: !!seg.is_manual,
                segmentation_variable:
                  seg.segmentation_variable ||
                  values.data_upload_segmentation_variable,
                variable_type: sVarType,
              };
            }),
          };
          api
            .post("case-import/generate-segment-values", confirmedSegPayload)
            .then((res) => {
              const generatedData = res.data;

              /// update currentCase global state with generated segments from generatedData
              // when successfully generated segment values
              if (generatedData?.segments?.length) {
                CurrentCaseState.update((s) => ({
                  ...s,
                  segments: generatedData.segments,
                  import_id: generatedData?.import_id || null,
                }));
              }
              // EOL

              // reset form and close drawer
              setTimeout(() => {
                form.resetFields();
                handleCancel();
                // always navigate to step 1 after save
                navigate(
                  `${routePath.idc.case}/${data.id}/${stepPath.step1.label}`
                );
              }, 1000);
            })
            .catch((e) => {
              console.error(e);
              const detail = e?.response?.data?.detail;
              const errorMessage =
                typeof detail === "string"
                  ? detail
                  : Array.isArray(detail)
                  ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
                  : typeof detail === "object" && detail !== null
                  ? JSON.stringify(detail)
                  : "Failed to generate segment values from import data.";

              messageApi.open({
                type: "error",
                content: errorMessage,
                duration: 5,
              });
            });
        } else {
          // NORMAL BEHAVIOUR
          setTimeout(() => {
            form.resetFields();
            handleCancel();
            // always navigate to step 1 after save
            navigate(
              `${routePath.idc.case}/${data.id}/${stepPath.step1.label}`
            );
          }, 100);
        }
        // EOL SAVE /case-import/generate-segment-values
      })
      .catch((e) => {
        console.error(e);
        const { status, data } = e.response;
        let errorText = "Failed to save case setting.";
        if (status === 403) {
          errorText = data.detail;
        }
        if (status === 400 && data?.detail?.includes("unique names")) {
          // should have unique segment name
          errorText = data.detail;
        }
        messageApi.open({
          type: "error",
          content: errorText,
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const onFinish = (values) => {
    const handleSave = () => {
      setIsSaving(true);
      setFormData(values);

      // handle if any segments deleted
      if (deletedSegmentIds?.length) {
        Promise.all(
          deletedSegmentIds.map((sId) => api.delete(`segment/${sId}`))
        )
          .then(() => {
            setDeletedSegmentIds([]);
            setTimeout(() => {
              saveCaseSettings(values, true);
            }, 100);
          })
          .catch((e) => {
            console.error(e);
            messageApi.open({
              type: "error",
              content: "Failed to delete segments.",
            });
            setIsSaving(false);
          });
      } else {
        saveCaseSettings(values);
      }
    };

    const hasSecondarySaved = currentCase?.case_commodities?.some(
      (c) => c.commodity_type === "secondary"
    );
    const hasTertiarySaved = currentCase?.case_commodities?.some(
      (c) => c.commodity_type === "tertiary"
    );

    const isRemovingSecondary = hasSecondarySaved && !secondary.enable;
    const isRemovingTertiary = hasTertiarySaved && !tertiary.enable;

    if (isRemovingSecondary || isRemovingTertiary) {
      let cropType = "";
      if (isRemovingSecondary && isRemovingTertiary) {
        cropType = "secondary and tertiary crops";
      } else if (isRemovingSecondary) {
        cropType = "secondary commodity";
      } else {
        cropType = "tertiary commodity";
      }

      Modal.confirm({
        title: `Are you sure you want to remove this ${cropType}?`,
        icon: <ExclamationCircleOutlined />,
        content: `Removing this ${cropType} will permanently delete all associated data. This action cannot be undone.`,
        okText: "Yes, remove it",
        okType: "danger",
        cancelText: "No, keep it",
        onOk: handleSave,
        onCancel: () => setIsSaving(false),
      });
    } else {
      handleSave();
    }
  };

  return (
    <Drawer
      title="Create new case"
      closable={false}
      onClose={handleCancelWithGuard}
      open={open}
      width="60%"
      className="case-settings-modal-container"
      maskClosable={false}
      destroyOnClose={true}
      extra={
        <Button icon={<CloseOutlined />} onClick={handleCancelWithGuard} />
      }
    >
      {contextHolder}
      <Form
        form={form}
        name="case-settings-form"
        layout="vertical"
        initialValues={formData}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
        autoComplete="off"
      >
        <CaseForm
          form={form}
          enableEditCase={enableEditCase}
          updateCurrentCase={updateCurrentCase}
          deletedSegmentIds={deletedSegmentIds}
          setDeletedSegmentIds={setDeletedSegmentIds}
          dataUploadFieldPreffix={dataUploadFieldPreffix}
          onTabChange={(key) => setActiveTab(key)}
          activeTab={activeTab}
          currentCase={currentCase}
        />
        <div className="case-form-button-wrapper">
          <Button onClick={handleCancelWithGuard} className="button-cancel">
            Cancel
          </Button>
          {isUploadMissing ? (
            <Tooltip title="Please upload a data template, or switch to 'Manual data input' to define segments to save this case.">
              <span className="cursor-not-allowed">
                <Button
                  loading={isSaving}
                  disabled={isSaveDisabled}
                  className="button-save"
                >
                  Save case
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              loading={isSaving}
              disabled={isSaveDisabled}
              className="button-save"
              onClick={() => form.submit()}
            >
              Save case
            </Button>
          )}
        </div>
      </Form>
    </Drawer>
  );
};

export default CaseSettings;
